import {useRouter} from 'next/router';
import {Flex, Button, Text, BoxProps, useDisclosure, useToast, UseToastOptions} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {VerifyNumberToJoin} from '@eksab/components';
import {useMeQuery, useRegionSettings} from '@eksab/hooks';
import {AuthModal} from '@eksab/features/auth/modals/AuthModal';

import {useCompetitionJoin, useCompetitionQuery} from '../hooks';
import {CompetitionType} from '../types';
import {lineupCache} from '../lineup/lineupCache';
import {useSubmitLineupMutation} from '../lineup/useSubmitLineupMutation';
import {ReferralModal} from './modals/ReferralModal';
import {BuyCoinsModal} from './modals';

export function JoinCompetitionButton(props: BoxProps) {
  const {asPath, push} = useRouter();
  const competitionID = useRouter().query.slug as string;
  const {isOpen: isBuyCoinsOpen, onOpen: onBuyCoinsOpen, onClose: onBuyCoinsClose} = useDisclosure();
  const {isOpen: isReferralModalOpen, onOpen: onReferralModalOpen, onClose: onReferralModalClose} = useDisclosure();
  const verifyNumberDisclosure = useDisclosure();
  const {onClose: _onAuthModalClose, ...restAuthDisclosure} = useDisclosure();
  const toast: (options?: UseToastOptions) => void = useToast();

  const t = useTranslate();
  const {data: me} = useMeQuery();
  const {data: {entry_fee: fees = 0, type, participants} = {}, isFetched, refetch} = useCompetitionQuery(competitionID);
  const submitLineupMutation = useSubmitLineupMutation(competitionID, {
    onSuccess: () => toast({status: 'success', title: t('fantasy.lineup.submit-after-join.success')}),
    onError: (error) => {
      toast({status: 'error', title: error.response?.data?.message ?? t('app.errors.try-again')});
    },
  });

  const {data: regionSettings} = useRegionSettings();

  const isCapped = !!participants?.max;
  const maxParticipantsReached = isCapped && participants?.max === participants?.current;

  const joinMutation = useCompetitionJoin({
    onSuccess: () => {
      toast({title: t('competition.join.success'), status: 'success', id: 'joined-successfully'});

      submitSavedLineup();
      skipOverview();
    },
  });

  const submitSavedLineup = () => {
    const savedLineup = lineupCache.getLineup(competitionID);
    if (!savedLineup) return;

    submitLineupMutation.mutate(savedLineup);
  };

  const skipOverview = () => {
    const isFantasy = type === CompetitionType.FANTASY;

    push(`/${isFantasy ? 'fantasy' : 'predictions'}/${competitionID}/${isFantasy ? 'lineup' : 'matches'}`);
  };

  const startJoinFlow = (coins: number) => {
    const canJoin = coins >= fees;
    if (!canJoin) {
      if (!regionSettings?.settings?.coin_packages_enabled && !regionSettings?.settings?.badge_packages_enabled)
        return onReferralModalOpen();
      return onBuyCoinsOpen();
    }

    mixpanel?.track('Join_competition', {competitionType: type, entryFee: fees});

    joinMutation.mutate(competitionID, {
      onError: (e) => toast({title: e.response?.data.message ?? t('competition.join.error'), status: 'error'}),
    });
  };

  const onAuthModalClose = async (me: Me | undefined) => {
    if (!me) return _onAuthModalClose();

    const isJoined = (await refetch()).data?.joined;
    if (isJoined) {
      toast({title: t('competition.join.info.joined-before'), status: 'info'});
      return skipOverview();
    }

    _onAuthModalClose();

    if (!me.phone_verified) return verifyNumberDisclosure.onOpen();

    startJoinFlow(me.coins);
  };
  const authDisclosure = {onClose: onAuthModalClose, ...restAuthDisclosure};

  const onJoin = () => {
    mixpanel?.track('Button Clicked', {button: 'competition.join_new', source: asPath});

    if (!competitionID) return toast({title: t('competition.wait'), status: 'info'});

    // TODO: remove competition.join.warning.not-joinable from app because Join/SubmitLineUp buttons should only be there if match is upcoming
    if (!me) return authDisclosure.onOpen();

    if (regionSettings?.settings.phone_number_verification_enabled && !me.phone_verified)
      return verifyNumberDisclosure.onOpen();

    startJoinFlow(me.coins);
  };

  return (
    <>
      <Flex flexDir={['column-reverse', null, 'column']} align="center" gap="2" {...props}>
        <Button
          w={['full', null, '20.625rem']}
          disabled={!isFetched || maxParticipantsReached}
          onClick={onJoin}
          data-testid="join-btn"
        >
          {t('app.actions.join')}
        </Button>

        {isCapped && (
          <Text color="gray.500" fontSize="0.625rem" textAlign="center">
            {t('competition.withdraw.not-withdrawable-brief')}
          </Text>
        )}
      </Flex>

      <VerifyNumberToJoin {...verifyNumberDisclosure} />
      <BuyCoinsModal isOpen={isBuyCoinsOpen} onClose={onBuyCoinsClose} fees={fees} />
      <ReferralModal isOpen={isReferralModalOpen} onClose={onReferralModalClose} />

      <AuthModal {...authDisclosure} />
    </>
  );
}
